/**
 * Replaces dynamic variables in a template string with provided values.
 *
 * This function takes a template string containing placeholders in the format `{{variableName}}`
 * and replaces them with corresponding values from the provided object. It supports multiple
 * variable replacements in a single template string.
 *
 * @param templateString - The template string containing placeholders in the format `{{variableName}}`
 * @param replacements - An object where keys are variable names (without braces) and values are the replacement strings
 *
 * @returns A new string with all placeholders replaced by their corresponding values
 *
 * @example
 * ```typescript
 * const templateString = "www.$(domain-name)/{{journal}}/{{primaryJournalSlug}}/for-authors/author-guidelines";
 * const replacements = {
 *   journal: "frontiers",
 *   primaryJournalSlug: "psychology"
 * };
 *
 * const result = replaceDynamicVars(templateString, replacements);
 * // Result: "www.$(domain-name)/frontiers/psychology/for-authors/author-guidelines"
 * ```
 */
export const replaceDynamicVars = (
  templateString: string,
  replacements: Record<string, string>
): string => {
  let result = templateString;

  // Replace each dynamic variable in the template
  Object.entries(replacements).forEach(([key, value]) => {
    const placeholder = `{{${key}}}`;
    result = result.replace(new RegExp(placeholder, "g"), value || "");
  });

  return result;
};
